import { type ReactNode } from 'react'
import cx from 'classnames'
import { isToday, isYesterday } from 'date-fns'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { useIsElementInView } from '@/hooks/element-hooks'
import { truncate } from 'lodash-es'
import { EditableMessageContent } from '@/features/messaging/components/ConversationView/Messages/List/EditableMessageContent'
import { type MessageEditProps } from '@/features/messaging/components/ConversationView/Messages/List/MessageGroup'
import styles from './BubbleMessageGroup.module.css'
import { dateFormatters } from '@/utils/date-util'

function dayLabel(date: Date) {
  if (isToday(date)) {
    return 'Today'
  }
  if (isYesterday(date)) {
    return 'Yesterday'
  }
  return dateFormatters.dateWeekday.format(date)
}

export interface BubbleMessageGroupProps {
  showDateLine: boolean
  timestamp: Date
  mine: boolean
  senderDisplayName: string
  senderAvatar: AvatarSubject
  tagLabel: string | null
  children?: ReactNode
}

/**
 * A sender's run of messages as chat bubbles: theirs on the left beside their
 * avatar, the viewer's on the right, each run captioned with who and when.
 */
export function BubbleMessageGroup({
  showDateLine,
  timestamp,
  mine,
  senderDisplayName,
  senderAvatar,
  tagLabel,
  children,
}: BubbleMessageGroupProps) {
  const caption = [
    mine ? 'You' : truncate(senderDisplayName, { length: 60 }),
    tagLabel,
    dateFormatters.timeDefault.format(timestamp),
  ]
    .filter(Boolean)
    .join(' - ')

  return (
    <div className={styles.root}>
      {showDateLine && <div className={styles.day}>{dayLabel(timestamp)}</div>}
      <div className={cx(styles.group, mine && styles.mine)}>
        {!mine && (
          <div className={styles.avatar}>
            <Avatar size={28} subject={senderAvatar} />
          </div>
        )}
        <div className={styles.stack}>
          {children}
          <div className={styles.caption}>{caption}</div>
        </div>
      </div>
    </div>
  )
}

export interface BubbleProps {
  mine: boolean
  isNote: boolean
  isText: boolean
  onInView: () => unknown
  edit: MessageEditProps
  content: ReactNode
}

export function Bubble({
  mine,
  isNote,
  isText,
  onInView,
  edit,
  content,
}: BubbleProps) {
  const { connectRef: ref } = useIsElementInView((isInView) => {
    if (isInView) {
      onInView()
    }
  })

  return (
    <div ref={ref} className={styles.row}>
      <EditableMessageContent {...edit}>
        <div
          className={cx(
            isText && styles.bubble,
            isText && mine && !isNote && styles.bubbleMine,
            isNote && styles.note,
          )}
        >
          {isNote && <div className={styles.noteTag}>Personal note</div>}
          {content}
        </div>
      </EditableMessageContent>
    </div>
  )
}
